// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Link, useLocation } from "react-router-dom";

// Local Imports
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import TabsComponent from "../../components/tabs-component/tabs-component";
import Pending from "./components/pending/pending";
import Completed from "./components/completed/completed";
import AllPortfolios from "./components/all-portfolios/all-portfolios";

// Style Imports
import "./portfolios.scss";
import { useDispatch, useSelector } from "react-redux";
import { CREATE_BREADCRUMB } from "../../store/actions";
import PortfolioAgreementComponent from "../portfolio-agreement/portfolio-agreement.component";
import {
  INITIATE_PERMISSION,
  REVIEW_PERMISSION,
} from "../portfolio-agreements-bulk/types";

const Portfolios: React.FC = (): ReactElement => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const canReviewAgreements = user?.permissions?.includes(REVIEW_PERMISSION);
  const queryParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState<string>(
    queryParams.get("tab") || "1"
  );
  const [exportClicked, setExportClicked] = useState<boolean>(false);

  useEffect(() => {
    dispatch({
      type: CREATE_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: "Portfolios",
            type: "link",
            link: "/portfolio",
          },
        ],
      },
    });
  }, []);

  const items: any = [
    {
      key: "1",
      label: `Pending`,
      children: <Pending />,
    },
    {
      key: "2",
      label: `Completed`,
      children: <Completed />,
    },
    {
      key: "3",
      label: `All`,
      children: <AllPortfolios />,
    },
    canReviewAgreements && {
      key: "4",
      label: `Portfolio agreements`,
      children: <PortfolioAgreementComponent exportClicked={exportClicked} />,
    },
    // A failed gate leaves `false` in the array, which antd would try to render.
  ].filter(Boolean);

  // A link to ?tab=4 would otherwise select a tab that is not rendered, leaving
  // the page blank. Falls back to the first tab, and re-checks in case the user
  // arrives before permissions are in the store.
  useEffect(() => {
    if (activeTab === "4" && !canReviewAgreements) setActiveTab("1");
  }, [activeTab, canReviewAgreements]);

  const onExportClicked = () => {
    setExportClicked(true);
  };

  const onTabChange = (activeKey: string): void => {
    setActiveTab(activeKey);
  };

  return (
    <div className="portfolios-container w-full flex flex-col">
      <div className="flex items-center justify-between flex-wrap">
        <div className="w-full flex items-center justify-between flex-wrap">
          <BreadcrumbComponent />
          {activeTab === "4" && canReviewAgreements && (
            <div className="portfolio-agreement-actions">
              {user?.permissions?.includes(INITIATE_PERMISSION) && (
                <Link to="/portfolio/agreements-bulk" className="bulk-link">
                  Bulk initiation
                </Link>
              )}
              <button
                onClick={onExportClicked}
                className="bg-blue-500 text-white px-5 py-2 rounded-md"
              >
                Export
              </button>
            </div>
          )}
        </div>
      </div>
      <TabsComponent
        items={items}
        activeTab={activeTab}
        onChange={onTabChange}
      />
    </div>
  );
};

export default Portfolios;
